import { AlertTriangle, Inbox, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { STATUS_MAP } from "@/lib/i18n";
import { fmtMoney } from "@/lib/format";

export function Clickable({ onClick, className, children, testId, style }) {
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } }} className={cn("cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary", className)} style={style} data-testid={testId}>
      {children}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between rise" data-testid={testId}>
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatusBadge({ status, label, className, testId }) {
  const s = STATUS_MAP[status];
  return (
    <span data-testid={testId} className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold", s?.cls || "bg-muted text-muted-foreground", className)}>
      {s && <span className={cn("h-1.5 w-1.5 rounded-full", s.dot, status === "in_repair" && "animate-pulse")} />}
      {label || s?.label || status}
    </span>
  );
}

export function Pill({ children, className, testId }) {
  return (
    <span data-testid={testId} className={cn("inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold", className)}>
      {children}
    </span>
  );
}

export function Money({ value, className }) {
  return <span className={cn("tabular", className)}>{fmtMoney(value)}</span>;
}

export function Loader({ label = "Chargement…", className }) {
  return (
    <div className={cn("flex items-center justify-center gap-3 py-16 text-sm text-muted-foreground", className)} data-testid="loading-state">
      <Loader2 className="h-5 w-5 animate-spin text-primary" /> {label}
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500" data-testid="error-banner">
      <span className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4" /> {message}
      </span>
      {onRetry && (
        <button onClick={onRetry} className="font-semibold underline underline-offset-4" data-testid="error-retry-button">
          Réessayer
        </button>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon = Inbox, title = "Aucun élément", text, action, testId = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-border px-6 py-12 text-center" data-testid={testId}>
      <Icon className="h-8 w-8 text-muted-foreground/60" />
      <p className="font-semibold">{title}</p>
      {text && <p className="max-w-sm text-sm text-muted-foreground">{text}</p>}
      {action}
    </div>
  );
}

export function KpiTile({ label, value, hint, icon: Icon, tone = "default", onClick, testId, delay = 0 }) {
  const tones = {
    default: "border-border/70",
    danger: "border-red-500/40 bg-red-500/5",
    warning: "border-amber-500/40 bg-amber-500/5",
    success: "border-emerald-500/40 bg-emerald-500/5",
    info: "border-blue-500/40 bg-blue-500/5",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{ animationDelay: `${delay}ms` }}
      className={cn("surface surface-hover rise flex min-h-[104px] flex-col justify-between p-4 text-left", tones[tone], !onClick && "cursor-default")}
    >
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div>
        <div className="font-heading text-3xl font-bold tabular">{value}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </div>
    </button>
  );
}

export function Section({ title, count, action, children, className, testId }) {
  return (
    <section className={cn("surface p-4 sm:p-5", className)} data-testid={testId}>
      {(title || action) && (
        <header className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold">
            {title}
            {count !== undefined && <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground tabular">{count}</span>}
          </h3>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({ label, children, hint, className }) {
  return (
    <label className={cn("flex flex-col gap-1.5 text-sm", className)}>
      <span className="font-medium text-muted-foreground">{label}</span>
      {children}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}

export function DataRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/50 py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("text-right font-medium", mono && "tabular")}>{value ?? "—"}</span>
    </div>
  );
}
